// @flow

import React, {
  forwardRef,
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@mui/styles';

import { grey } from '@mui/material/colors';
import Radio from '@mui/material/Radio';
import { PRESCRIPTION_METERING, PRESCRIPTION_UNIT } from 'js/constants/PrescriptionConstants';
import Grid from '@mui/material/Grid';
import { useLangFile } from 'js/context/LanguageContext';
import { MetaTypes } from 'js/components/Prescription/PrescriptionJob';
import PrescriptionAutoAdjustWarningDialog from 'js/components/Prescription/Dialogs/PrescriptionAutoAdjustWarningDialog';
import {
  DEFAULT_MAX_PRESCRIPTION,
  hasRequiredLimeLayers,
} from 'js/components/Prescription/PrescriptionUtils';
import { TEXT_FORM_STATE } from '../../../UI-Elements/TextFormStateControl';
import CachedIcon from '@mui/icons-material/Cached';
import { Box, FormControlLabel, Tooltip, Typography } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ToleranceInfoDialog from '../ToleranceInfoDialog/ToleranceInfoDialog';
import { SURVEY_LAYERS } from '../../../../constants/SurveyLayers';
import SeedingCalc from './SeedingCalculator/SeedingCalculator';
import { setSeedingCalculatorParams } from '../../../../reducers/PrescriptionReducer';
import MissingSoilAnalysisTooltip from './SeedingCalculator/MissingSoilAnalysisTooltip';
import { useStore } from 'react-redux';
import { AnalyticsProvider } from '../../../../context/AnalyticsContext';
import {
  Button,
  Checkbox,
  IconButton,
  TrackedLiveTextFormControl,
  TrackedRadioGroup,
  TrackedSelectFormControl,
  TrackedTextFormControl,
  TrackedTextFormStateControl,
} from '../../../Analytics/TrackedComponents';
import { ANALYTICS_EVENTS } from '../../../Analytics/AnalyticsConstants';
import { useFeatureFlags } from '../../../../featureFlags/FeatureFlagContext';
import { NEW_SEEDING_MODEL_FLAG } from '../../../../featureFlags/featureFlags';
import { useSeedingCalculatorStrategy } from './SeedingCalculator/SeedingCalculatorStrategy/useSeedingCalculatorStrategy';

const validRegex = /^[a-zA-Z0-9]+$/;
const warningRegex = /^[a-zA-Z0-9æøåÆØÅ\-_()%,]+$/;
const seedingFeatureCountries = ['DK', 'EE', 'LT'];

const styles = (theme) => ({
  root: {
    backgroundColor: 'white',
    color: grey['600'],
    display: 'flex',
    textAlign: 'left',
    flex: 1,
    flexDirection: 'column',
    justifyItems: 'center',
    fontSize: '0.8em',
  },
  control: {
    padding: theme.spacing(1),
  },
  label: {
    fontSize: '1rem',
  },
  limeEfficiencyInput: {
    paddingBottom: '12px',
  },
  '@media (max-width: 1024px), (max-height: 800px)': {
    control: {
      padding: `0 calc(${theme.spacing(1)} / 2)`,
      paddingTop: '8px',
    },
    label: {
      fontSize: '0.75rem',
    },
    limeEfficiencyInput: {
      paddingBottom: '4px',
    },
  },
});

const PrescriptionSettings = forwardRef(
  (
    {
      classes,
      onError,
      onUpdateMaxPrescription,
      onUpdateUnit,
      fieldSize,
      metering,
      unit,
      metaType,
      jobName,
      limeInfo,
      onUpdateJobName,
      onUpdateMetaType,
      maxPrescription,
      onUpdateMetering,
      onUpdateLimeInfo,
      isDisabled,
      useManualLimeEfficiencyInput,
      onUpdateUseManualEfficiencyLimeInput,
      assets,
      isBulkPrescription,
      bulkPrescriptionFields,
      surveys,
      dispatch,
      adjustPrescriptionBasedOnSoilAnalysis,
      setAdjustPrescriptionBasedOnSoilAnalysis,
    },
    ref
  ) => {
    const LangFile = useLangFile();
    const [pendingMetaType, setPendingMetaType] = useState(null);
    const [limeEfficiency, setLimeEfficiency] = useState(null);
    const [showToleranceInfo, setShowToleranceInfo] = useState(false);
    const [limeSuppliers, setLimeSuppliers] = useState([]);
    const [selectedLimeSupplierId, setSelectedLimeSupplierId] = useState(null);
    const farm = useStore().getState().farm.farm;
    const appliedInitialIntervals =
      useStore().getState().prescription.bulkPrescription.appliedInitialIntervals;
    const seedingCalculatorStrategy = useSeedingCalculatorStrategy();

    const isSeedingJob = useMemo(() => metaType === MetaTypes.SEEDING, [metaType]);

    const limeMin = 3000;
    const limeMax = 6000;

    const DEFAULT_UNIT_OPTIONS = [
      {
        value: PRESCRIPTION_UNIT.KILOGRAM,
        label: LangFile.PrescriptionSettings.unit.kilogram.name,
      },
      {
        value: PRESCRIPTION_UNIT.LITER,
        label: LangFile.PrescriptionSettings.unit.liters.name,
      },
    ];

    const metaTypeToUnitOptions = {
      [MetaTypes.FERTILIZING]: DEFAULT_UNIT_OPTIONS,
      [MetaTypes.SEEDING]: [
        {
          value: PRESCRIPTION_UNIT.KILOGRAM,
          label: LangFile.PrescriptionSettings.unit.kilogram.name,
        },
        {
          value: PRESCRIPTION_UNIT.PIECES_M2,
          label: LangFile.PrescriptionSettings.unit.pieces.name,
        },
      ],
      [MetaTypes.LIME]: [
        {
          value: PRESCRIPTION_UNIT.KILOGRAM,
          label: LangFile.PrescriptionSettings.unit.kilogram.name,
        },
      ],
      [MetaTypes.SPRAYING]: DEFAULT_UNIT_OPTIONS,
      [MetaTypes.SPOT_SPRAYING]: DEFAULT_UNIT_OPTIONS,
      [MetaTypes.LEGACY]: DEFAULT_UNIT_OPTIONS,
      '': DEFAULT_UNIT_OPTIONS,
    };

    const unitOptions = metaTypeToUnitOptions[metaType];

    const enableUnitSelection =
      metaType === MetaTypes.FERTILIZING || metaType === MetaTypes.SEEDING;

    const selectedJobType = useMemo(() => {
      return metaType ? metaType : '';
    }, [metaType]);

    const disableLimeJob =
      assets[SURVEY_LAYERS.RT] == undefined ||
      assets[SURVEY_LAYERS.HUMUS] == undefined ||
      assets[SURVEY_LAYERS.CLAY] == undefined ||
      (isBulkPrescription &&
        bulkPrescriptionFields.some((field) => {
          const fieldSurveys = surveys.filter((survey) => survey.fieldId === field.fieldId);

          return !hasRequiredLimeLayers(fieldSurveys);
        }));

    let maxPrescriptionEndAdornment;
    switch (unit) {
      case PRESCRIPTION_UNIT.LITER:
        maxPrescriptionEndAdornment = LangFile.PrescriptionSettings.unit.liters.short;
        break;
      case PRESCRIPTION_UNIT.PIECES_M2:
        maxPrescriptionEndAdornment = LangFile.PrescriptionSettings.unit.pieces.short;
        break;
      default:
        maxPrescriptionEndAdornment = LangFile.PrescriptionSettings.unit.kilogram.short;
    }

    let maxPrescriptionLabel;
    switch (metering) {
      case PRESCRIPTION_METERING.HA:
        maxPrescriptionLabel = isSeedingJob
          ? LangFile.PrescriptionSettings.desiredSeedingAmount
              .desiredAllocationDescriptionPerHectare
          : LangFile.PrescriptionSettings.maxAllocation.maxAllocationDescriptionPerHectare;
        maxPrescriptionEndAdornment += ` / ha`;
        break;
      case PRESCRIPTION_METERING.SQUARE_METER:
        maxPrescriptionLabel = isSeedingJob
          ? LangFile.PrescriptionSettings.desiredSeedingAmount
              .desiredAllocationDescriptionPerSquareMeter
          : LangFile.PrescriptionSettings.maxAllocation.maxAllocationDescriptionPerSquareMeter;
        maxPrescriptionEndAdornment += ` / m\u00B2`;
        break;
      default:
        maxPrescriptionLabel = isSeedingJob
          ? LangFile.PrescriptionSettings.desiredSeedingAmount.desiredAllocationDescription
          : LangFile.PrescriptionSettings.maxAllocation.maxAllocationDescription;
        maxPrescriptionEndAdornment += ` total`;
    }

    const danishLimeEfficiencyStrategy = (chosenEfficiency: number): number => {
      if (chosenEfficiency >= 80) {
        return 100;
      }

      return (chosenEfficiency / 80) * 100;
    };

    const updateLimeSupplierEfficiency = (supplierId) => {
      const selectedSupplier = limeSuppliers.find((s) => s.id === supplierId);

      setSelectedLimeSupplierId(supplierId);
      setLimeEfficiency(null);

      onError('limeSupplierEfficiency', false);
      onError('manualLimeEfficiency', true);

      onUpdateLimeInfo({ efficiency: danishLimeEfficiencyStrategy(selectedSupplier.value) });
    };

    const { isEnabled } = useFeatureFlags();
    const newSeedingModelEnabled = isEnabled(NEW_SEEDING_MODEL_FLAG);
    const multiLayerSeedingModelAvailable =
      newSeedingModelEnabled && seedingFeatureCountries.includes(farm?.country);

    const seedingCalcAvailable = useMemo(() => {
      return seedingCalculatorStrategy !== null;
    }, [seedingCalculatorStrategy]);

    const handleOnUpdateUnit = useCallback(
      (value) => {
        onUpdateUnit(value);
      },
      [onUpdateUnit]
    );

    const handleOnUpdateMaxPrescription = useCallback(
      (value) => {
        onUpdateMaxPrescription(value);
      },
      [onUpdateMaxPrescription]
    );

    const handleOnClickUseCalcResult = useCallback(
      (seedingCalcResult) => {
        handleOnUpdateUnit(PRESCRIPTION_UNIT.KILOGRAM);
        onUpdateMetering(PRESCRIPTION_METERING.HA);
        handleOnUpdateMaxPrescription(seedingCalcResult);
      },
      [handleOnUpdateMaxPrescription, handleOnUpdateUnit, onUpdateMetering]
    );

    const updateLimeEfficiency = (limeEfficiency) => {
      onError('limeSupplierEfficiency', true);
      setLimeEfficiency(limeEfficiency);
      onUpdateLimeInfo({ efficiency: danishLimeEfficiencyStrategy(limeEfficiency) });
    };

    const handleOnError = (key) =>
      useCallback(
        (error) => {
          onError(key, error);
        },
        [onError]
      );

    const handleOnUpdateJobName = useCallback(
      (value) => {
        onUpdateJobName(value);
      },
      [onUpdateJobName]
    );

    const handleSetAdjustPrescriptionBasedOnSoilAnalysis = useCallback(
      (event) => {
        setAdjustPrescriptionBasedOnSoilAnalysis(event.target.checked);
      },
      [setAdjustPrescriptionBasedOnSoilAnalysis]
    );

    const handleOnUpdateMetaType = useCallback(
      (value) => {
        dispatch(setSeedingCalculatorParams(null));

        if (value !== pendingMetaType) {
          if (value === MetaTypes.SPOT_SPRAYING) {
            setPendingMetaType(value);
            return;
          }
        }

        onUpdateMetaType(value);

        const metaTypeToUnit = {
          [MetaTypes.FERTILIZING]: PRESCRIPTION_UNIT.KILOGRAM,
          [MetaTypes.SEEDING]: PRESCRIPTION_UNIT.KILOGRAM,
          [MetaTypes.LIME]: PRESCRIPTION_UNIT.KILOGRAM,
          [MetaTypes.SPRAYING]: PRESCRIPTION_UNIT.LITER,
          [MetaTypes.SPOT_SPRAYING]: PRESCRIPTION_UNIT.LITER,
          [MetaTypes.LEGACY]: PRESCRIPTION_UNIT.LITER,
        };

        const unit = metaTypeToUnit[value];

        handleOnUpdateUnit(unit);

        if (value === MetaTypes.LIME) {
          onUpdateMetering(PRESCRIPTION_METERING.HA);
        }
      },
      [onUpdateMetaType, pendingMetaType, handleOnUpdateUnit]
    );

    const handleOnCancelMetaType = useCallback(() => {
      setPendingMetaType(null);
    }, []);

    const handleOnUpdateMetering = useCallback(
      (event) => {
        onUpdateMetering(event.target.value);
      },
      [onUpdateMetering]
    );

    const state = useCallback((val) => {
      if (val === null || val === undefined) {
        return {
          state: TEXT_FORM_STATE.INVALID,
          message: LangFile.PrescriptionSettings.name.errorText,
        };
      } else if (val === '') {
        return {
          state: TEXT_FORM_STATE.EMPTY,
          message: null,
        };
      } else if (validRegex.test(val)) {
        return {
          state: TEXT_FORM_STATE.VALID,
          message: null,
        };
      } else if (warningRegex.test(val)) {
        return {
          state: TEXT_FORM_STATE.WARNING,
          message: LangFile.PrescriptionSettings.name.warningText,
        };
      }
      return {
        state: TEXT_FORM_STATE.INVALID,
        message: LangFile.PrescriptionSettings.name.invalidText,
      };
    }, []);

    const hasAppliedInitialIntervalsInBulkOverview = useMemo(() => {
      return isBulkPrescription && appliedInitialIntervals;
    }, [appliedInitialIntervals, isBulkPrescription]);

    const onlyMaxInput = (
      <Grid size={8}>
        <TrackedTextFormControl
          data-testid="prescription-amount"
          {...ANALYTICS_EVENTS.textField(
            isSeedingJob ? 'desired_seeding_amount' : 'max_prescription'
          )}
          value={Number.isNaN(maxPrescription) ? '' : maxPrescription}
          type={'number'}
          min={0}
          isDisabled={isDisabled}
          required={true}
          placeholder={DEFAULT_MAX_PRESCRIPTION}
          className={classes.control}
          title={
            isSeedingJob
              ? LangFile.PrescriptionSettings.desiredSeedingAmount.title
              : LangFile.PrescriptionSettings.maxAllocation.title
          }
          label={maxPrescriptionLabel}
          endAdornment={maxPrescriptionEndAdornment}
          errorText={
            isSeedingJob
              ? LangFile.PrescriptionSettings.desiredSeedingAmount.errorText
              : LangFile.PrescriptionSettings.maxAllocation.errorText
          }
          onError={handleOnError('maxPrescription')}
          onUpdate={handleOnUpdateMaxPrescription}
        >
          <TrackedRadioGroup
            {...ANALYTICS_EVENTS.selector('prescription_metering')}
            aria-label={'position'}
            name={'position'}
            value={metering}
            onChange={handleOnUpdateMetering}
            row
          >
            <FormControlLabel
              value={
                unit === PRESCRIPTION_UNIT.PIECES_M2
                  ? PRESCRIPTION_METERING.SQUARE_METER
                  : PRESCRIPTION_METERING.HA
              }
              control={<Radio color="primary" />}
              classes={{ label: classes.label }}
              label={
                unit === PRESCRIPTION_UNIT.PIECES_M2
                  ? LangFile.PrescriptionSettings.maxAllocation.perSquareMeter
                  : LangFile.PrescriptionSettings.maxAllocation.perHectare
              }
              labelPlacement="end"
            />
            <FormControlLabel
              value={PRESCRIPTION_METERING.TOTAL}
              control={
                <Radio
                  color="primary"
                  slotProps={{ input: { 'data-testid': 'prescription-metering-total' } }}
                />
              }
              classes={{ label: classes.label }}
              label={LangFile.PrescriptionSettings.maxAllocation.entireField}
              labelPlacement="end"
              disabled={isBulkPrescription}
            />
          </TrackedRadioGroup>
        </TrackedTextFormControl>
      </Grid>
    );

    const limeEfficiencyInput = (
      <TrackedTextFormControl
        {...ANALYTICS_EVENTS.textField('lime_manual_efficiency')}
        value={limeEfficiency ? limeEfficiency : ''}
        className={classes.control}
        type={'number'}
        title={LangFile.PrescriptionSettings.limeInfo.efficiency.title}
        label={LangFile.PrescriptionSettings.limeInfo.efficiency.manual.label}
        onUpdate={updateLimeEfficiency}
        placeholder={LangFile.PrescriptionSettings.limeInfo.efficiency.manual.placeholder}
        min={0}
        max={100}
        acceptZero={true}
        helperText={
          limeEfficiency && (limeEfficiency > 100 || limeEfficiency < 0)
            ? LangFile.PrescriptionSettings.limeInfo.efficiency.manual.errorText
            : ''
        }
        onError={handleOnError('manualLimeEfficiency')}
      />
    );

    const limeEfficiencySelect = (
      <TrackedSelectFormControl
        {...ANALYTICS_EVENTS.selector('lime_supplier')}
        data-testid="lime-efficiency"
        className={classes.control}
        title={LangFile.PrescriptionSettings.limeInfo.efficiency.title}
        label={LangFile.PrescriptionSettings.limeInfo.efficiency.supplier.label}
        value={selectedLimeSupplierId || ''}
        onChange={updateLimeSupplierEfficiency}
        placeholder={LangFile.PrescriptionSettings.limeInfo.efficiency.supplier.placeholder}
        options={limeSuppliers.map((supplier) => ({
          value: supplier.id,
          label: supplier.label,
        }))}
        onError={handleOnError('limeSupplierEfficiency')}
      />
    );

    const limeJobInputs = (
      <Grid container size={8}>
        <Grid size={6}>
          <TrackedLiveTextFormControl
            {...ANALYTICS_EVENTS.textField('lime_max_prescription')}
            data-testid={'lime-max-prescription'}
            value={Number.isNaN(maxPrescription) ? '' : maxPrescription}
            type={'number'}
            title={LangFile.PrescriptionSettings.limeInfo.max.title}
            label={LangFile.PrescriptionSettings.limeInfo.max.label}
            onChange={handleOnUpdateMaxPrescription}
            size="small"
            className={classes.control}
            onUpdate={() => {}}
            placeholder={LangFile.PrescriptionSettings.limeInfo.max.placeholder}
            min={limeMin}
            max={limeMax}
            helperText={
              Number.isNaN(maxPrescription) &&
              (maxPrescription > limeMax || maxPrescription < limeMin)
                ? LangFile.PrescriptionSettings.limeInfo.max.errorText
                : ''
            }
            acceptZero={true}
            onError={handleOnError('maxPrescription')}
          />
        </Grid>

        <Grid size={6}>
          <Box
            sx={{
              position: 'relative',
            }}
          >
            <TrackedSelectFormControl
              {...ANALYTICS_EVENTS.selector('lime_tolerance')}
              title={LangFile.PrescriptionSettings.limeInfo.tolerance.title}
              className={classes.control}
              label={LangFile.PrescriptionSettings.limeInfo.tolerance.label}
              value={limeInfo && limeInfo.tolerance ? limeInfo.tolerance : 1}
              onChange={(value) => onUpdateLimeInfo({ tolerance: value })}
              placeholder={LangFile.PrescriptionSettings.limeInfo.max.placeholder}
              options={[
                {
                  value: 0.965,
                  label: `${LangFile.PrescriptionSettings.limeInfo.tolerance.options.tolerant} (AR = 0.965)`,
                },
                {
                  value: 1,
                  label: `${LangFile.PrescriptionSettings.limeInfo.tolerance.options.middle} (AR = 1.00)`,
                },
                {
                  value: 1.035,
                  label: `${LangFile.PrescriptionSettings.limeInfo.tolerance.options.sensitive} (AR = 1.035)`,
                },
              ]}
            />
            <Box
              sx={{
                position: 'absolute',
                right: '4px',
                top: '0px',
              }}
            >
              <Tooltip title={LangFile.PrescriptionSettings.limeInfo.tolerance.tooltip}>
                <IconButton
                  onClick={() => setShowToleranceInfo(true)}
                  {...ANALYTICS_EVENTS.button('lime_tolerance_info')}
                >
                  <InfoIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Grid>

        <ToleranceInfoDialog open={showToleranceInfo} onClose={() => setShowToleranceInfo(false)} />

        <Grid size={6}>
          {!useManualLimeEfficiencyInput && limeEfficiencySelect}

          {useManualLimeEfficiencyInput && limeEfficiencyInput}
        </Grid>

        <Grid
          container
          size={6}
          className={classes.limeEfficiencyInput}
          sx={{
            alignItems: 'flex-end',
          }}
        >
          <Grid size={12}>
            <Button
              {...ANALYTICS_EVENTS.button('lime_efficiency_input_mode', {
                mode: useManualLimeEfficiencyInput ? 'supplier' : 'manual',
              })}
              onClick={() => onUpdateUseManualEfficiencyLimeInput(!useManualLimeEfficiencyInput)}
              variant={'outlined'}
              style={{ color: 'black' }}
            >
              {useManualLimeEfficiencyInput
                ? LangFile.PrescriptionSettings.limeInfo.efficiency.buttonTextSupplier
                : LangFile.PrescriptionSettings.limeInfo.efficiency.buttonTextManual}
              {<CachedIcon style={{ marginLeft: '12px' }} />}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    );

    useEffect(() => {
      if (metaType !== MetaTypes.LIME) {
        return;
      }

      if (limeSuppliers.length > 0) {
        return;
      }

      const efficiencyLabel = LangFile.PrescriptionSettings.limeInfo.efficiency.supplier.effect;

      // Fetch limeSuppliers from backend
      const limeInfoSuppliers = [
        {
          id: 'kongerslev',
          value: 75,
          label: `Kongerslev kalk, harpet (75% ${efficiencyLabel})`,
        },
        {
          id: 'hillerslev',
          value: 70,
          label: `Hillerslev, harpet (70% ${efficiencyLabel})`,
        },
        {
          id: 'faxe',
          value: 80,
          label: `Faxe kalk, knust (80% ${efficiencyLabel})`,
        },
        {
          id: 'mg2.5',
          value: 80,
          label: `Mg-kalk, 2,5% (80% ${efficiencyLabel})`,
        },
        {
          id: 'mg5',
          value: 85,
          label: `Mg-kalk, 5% (85% ${efficiencyLabel})`,
        },
        {
          id: 'dolomit',
          value: 90,
          label: `Dolomitkalk, 10%, (90% ${efficiencyLabel})`,
        },
        {
          id: 'carbo_unprocessed',
          value: 38,
          label: `Carbokalk, ubehandlet (38% ${efficiencyLabel})`,
        },
        {
          id: 'carbo_pressed',
          value: 47,
          label: `Carbokalk, presset (47% ${efficiencyLabel})`,
        },
      ];

      setLimeSuppliers(limeInfoSuppliers);
    }, [metaType]);

    return (
      <AnalyticsProvider contextParams={{ source: 'prescription-settings' }}>
        <Fragment>
          <div className={classes.root}>
            <Grid container>
              <Grid size={8}>
                <TrackedTextFormStateControl
                  {...ANALYTICS_EVENTS.textField('prescription_job_name')}
                  data-testid={'prescription-job-name'}
                  isDisabled={isDisabled}
                  autoFocus
                  value={jobName}
                  required={true}
                  className={classes.control}
                  title={
                    isBulkPrescription
                      ? LangFile.PrescriptionSettings.name.bulkTitle
                      : LangFile.PrescriptionSettings.name.title
                  }
                  label={
                    isBulkPrescription
                      ? LangFile.PrescriptionSettings.name.bulkLabel
                      : LangFile.PrescriptionSettings.name.label
                  }
                  placeholder={LangFile.PrescriptionSettings.name.placeholder}
                  onError={handleOnError('jobName')}
                  helperText={LangFile.PrescriptionSettings.name.helperText}
                  onUpdate={handleOnUpdateJobName}
                  currentState={state}
                  errorStates={[TEXT_FORM_STATE.INVALID, TEXT_FORM_STATE.EMPTY]}
                />
              </Grid>
              <Grid size={4}>
                <TrackedSelectFormControl
                  disabled={isDisabled || hasAppliedInitialIntervalsInBulkOverview}
                  className={classes.control}
                  title={LangFile.PrescriptionSettings.jobType}
                  label={LangFile.PrescriptionSettings.sprayTaskLabel}
                  value={selectedJobType}
                  onChange={handleOnUpdateMetaType}
                  {...ANALYTICS_EVENTS.selector('job_type')}
                  data-testid="job-type"
                  options={[
                    {
                      value: MetaTypes.FERTILIZING,
                      label: LangFile.PrescriptionJob.metaTypes.fertilizing,
                    },
                    {
                      value: MetaTypes.SPRAYING,
                      label: LangFile.PrescriptionJob.metaTypes.spraying,
                    },
                    {
                      value: MetaTypes.SPOT_SPRAYING,
                      label: LangFile.PrescriptionJob.metaTypes.spotSpraying,
                    },
                    {
                      value: MetaTypes.SEEDING,
                      label: LangFile.PrescriptionJob.metaTypes.seeding,
                    },
                    {
                      value: MetaTypes.LIME,
                      label: LangFile.PrescriptionJob.metaTypes.lime,
                      disabled: disableLimeJob,
                    },
                  ]}
                />
              </Grid>

              {metaType === MetaTypes.SEEDING && multiLayerSeedingModelAvailable && (
                <Grid size={12}>
                  <MissingSoilAnalysisTooltip>
                    <FormControlLabel
                      control={
                        <Checkbox
                          slotProps={{ input: { 'data-testid': 'seeding-model-enabled' } }}
                          checked={adjustPrescriptionBasedOnSoilAnalysis}
                          onChange={handleSetAdjustPrescriptionBasedOnSoilAnalysis}
                          {...ANALYTICS_EVENTS.checkbox(
                            'adjust_prescription_based_on_soil_analysis'
                          )}
                        />
                      }
                      label={
                        LangFile.PrescriptionSettings.adjustDesiredPrescriptionBasedOnSoilAnalysis
                      }
                    />
                  </MissingSoilAnalysisTooltip>
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 4, mt: -0.5 }}>
                    {LangFile.PrescriptionSettings.croplineSeedingModelHelp}
                  </Typography>
                </Grid>
              )}

              {metaType === MetaTypes.SEEDING && seedingCalcAvailable && (
                <Grid size={12} sx={{ pt: 2 }}>
                  <SeedingCalc
                    handleOnClickUseCalcResult={handleOnClickUseCalcResult}
                    isBulkPrescription={isBulkPrescription}
                    adjustPrescriptionBasedOnSoilAnalysis={adjustPrescriptionBasedOnSoilAnalysis}
                    ref={ref}
                    dispatch={dispatch}
                    strategy={seedingCalculatorStrategy}
                  />
                </Grid>
              )}

              {metaType !== MetaTypes.LIME ? onlyMaxInput : limeJobInputs}

              <Grid size={4}>
                <TrackedSelectFormControl
                  data-testid="prescription-unit"
                  {...ANALYTICS_EVENTS.selector('prescription_unit')}
                  className={classes.control}
                  disabled={!enableUnitSelection || isDisabled}
                  title={LangFile.PrescriptionSettings.unit.title}
                  label={LangFile.PrescriptionSettings.unit.label}
                  value={unit ? unit : ''}
                  onChange={handleOnUpdateUnit}
                  options={unitOptions}
                />
              </Grid>
            </Grid>
          </div>

          <PrescriptionAutoAdjustWarningDialog
            pendingMetaType={pendingMetaType}
            onProceed={handleOnUpdateMetaType}
            onCancel={handleOnCancelMetaType}
          />
        </Fragment>
      </AnalyticsProvider>
    );
  }
);

export default memo(withStyles(styles)(PrescriptionSettings));

PrescriptionSettings.propTypes = {
  maxPrescription: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  metering: PropTypes.string,
  unit: PropTypes.string,
  jobName: PropTypes.string,
  metaType: PropTypes.string,
  fieldSize: PropTypes.number,
  onError: PropTypes.func,
  onUpdateMetaType: PropTypes.func,
  onUpdateMaxPrescription: PropTypes.func,
  onUpdateMetering: PropTypes.func,
  onUpdateJobName: PropTypes.func,
  onUpdateUnit: PropTypes.func,
  isDisabled: PropTypes.bool,
  isBulkPrescription: PropTypes.bool,
  bulkPrescriptionFields: PropTypes.array,
  surveys: PropTypes.array,
};

PrescriptionSettings.defaultProps = {
  // Settings default to null to avoid rendering, if the setting is not passed down from the container.
  metering: null,
  unit: null,
  jobName: null,
  task: null,
};
